import React, { useState } from 'react';
import { Box, Typography, TextField, InputAdornment } from '@mui/material';
// import { useTheme } from '@mui/material/styles';

/**
 * Component for displaying and editing deferred cost
 *
 * @param {Object} props - Component props
 * @param {Object} props.deficiency - Deficiency object containing deferredCost
 * @param {boolean} props.isEditing - Whether edit mode is active
 * @param {Function} props.onChange - Callback when cost is changed
 * @param {boolean} props.readOnly - Whether the component is in read-only mode
 * @returns {React.ReactElement} Deferred cost section component
 */
const DeferredCostSection = ({ deficiency, isEditing, onChange, readOnly = false }) => {
    const [error, setError] = useState('');
    const [inputCost, setInputCost] = useState(deficiency?.deferredCost || '');

    // Extract cost from deficiency object
    const cost = deficiency?.deferredCost;

    const handleCostChange = (event) => {
        const value = event.target.value;

        // Similar validation to DeferredCostModal, but allowing decimal-first inputs
        if (
            value === '' ||
            value === '.' ||
            /^\d+\.?\d{0,2}$/.test(value) ||
            /^\.\d{1,2}$/.test(value)
        ) {
            setInputCost(value);
            setError('');

            // Only update if it's a valid number
            if (value !== '.' && value !== '') {
                onChange(Number(value));
            }
        } else {
            setError('Please enter a valid cost');
        }
    };

    const handleKeyDown = (event) => {
        // Prevent 'e', 'E', '+', '-' from being entered
        if (['e', 'E', '+', '-'].includes(event.key)) {
            event.preventDefault();
        }
    };

    const formattedCost = cost ? Number(cost).toFixed(2) : '0.00';

    return (
        <Box>
            <Typography
                variant="caption"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    color: '#666666',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.75,
                    display: 'block',
                }}
            >
                Deferred Cost
            </Typography>

            {isEditing && !readOnly ? (
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={inputCost}
                    onChange={handleCostChange}
                    onKeyDown={handleKeyDown}
                    error={!!error}
                    helperText={error}
                    placeholder="0.00"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                    $
                                </Typography>
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{
                        inputMode: 'decimal',
                        pattern: '\\d*\\.?\\d{0,2}',
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backgroundColor: '#FFFFFF',
                        },
                    }}
                />
            ) : (
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#888888',
                        }}
                    >
                        $
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: '#333333',
                            lineHeight: 1,
                        }}
                    >
                        {formattedCost}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default DeferredCostSection;
